import React, { useState, useEffect, useCallback } from 'react';
import {
  Info, Plus, Pencil, Trash2, Loader2, X, Save,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  RefreshCw, Users, Target, Building2, Star
} from 'lucide-react';
import api from '../../utils/api';
import ImageUpload from '../../components/admin/ImageUpload';

/* ── Shared styles ──────────────────────────────────────────────────────────── */
const inp = "w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white";
const lbl = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

/* ── Collapsible section wrapper ────────────────────────────────────────────── */
const Section = ({ title, subtitle, icon: Icon, count, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors text-left">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-royal-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-royal-800" />
          </div>
          <div>
            <div className="font-display font-semibold text-gray-900 flex items-center gap-2">
              {title}
              {count !== undefined && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-semibold">{count}</span>
              )}
            </div>
            {subtitle && <div className="text-xs text-gray-400 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {open ? <ChevronUp size={18} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && <div className="border-t border-gray-100 p-5">{children}</div>}
    </div>
  );
};

/* ── Delete confirm ─────────────────────────────────────────────────────────── */
const DeleteModal = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trash2 size={22} className="text-red-600" />
      </div>
      <h3 className="font-display font-semibold text-gray-900 mb-2">Delete this item?</h3>
      <p className="text-gray-500 text-sm mb-5">This cannot be undone.</p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
        <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold">Delete</button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════════════
   TEAM MEMBERS
══════════════════════════════════════════════════════════════════════════════ */
const TeamSection = () => {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [msg,      setMsg]      = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/about/admin/team')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const blank = { name:'', role:'', area:'', initials:'', avatar:'', bio:'', email:'', linkedin:'', isPublished:true, order:0 };
  const openEdit = (item) => { setEditing(item._id || 'new'); setForm(item._id ? {...item} : blank); setMsg(''); };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      if (editing === 'new') await api.post('/about/admin/team', form);
      else await api.put(`/about/admin/team/${editing}`, form);
      setMsg('success'); fetch();
      setTimeout(() => { setEditing(null); setMsg(''); }, 900);
    } catch (e) { setMsg('error:' + (e.response?.data?.message || 'Save failed')); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/about/admin/team/${id}`); setDeleting(null); fetch(); };

  return (
    <>
      {deleting && <DeleteModal onConfirm={() => del(deleting)} onCancel={() => setDeleting(null)} />}
      <div className="flex justify-end mb-4">
        <button onClick={() => openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Member</button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">{editing === 'new' ? 'New Team Member' : 'Edit Team Member'}</h4>
            <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          {msg.startsWith('error:') && <div className="mb-3 flex items-center gap-2 text-red-600 text-sm"><AlertTriangle size={14}/>{msg.slice(6)}</div>}
          {msg === 'success' && <div className="mb-3 flex items-center gap-2 text-emerald-600 text-sm"><CheckCircle size={14}/>Saved!</div>}

          <ImageUpload
            label="Photo (optional)"
            value={form.avatar || ''}
            onChange={url => setForm(p => ({...p, avatar: url}))}
          />

          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><label className={lbl}>Full Name *</label><input className={inp} value={form.name||''} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></div>
            <div><label className={lbl}>Initials (for avatar fallback)</label><input className={inp} placeholder="e.g. AG" maxLength={3} value={form.initials||''} onChange={e=>setForm(p=>({...p,initials:e.target.value}))}/></div>
            <div><label className={lbl}>Role / Title *</label><input className={inp} value={form.role||''} onChange={e=>setForm(p=>({...p,role:e.target.value}))}/></div>
            <div><label className={lbl}>Specialisation / Area</label><input className={inp} value={form.area||''} onChange={e=>setForm(p=>({...p,area:e.target.value}))}/></div>
            <div><label className={lbl}>Email</label><input type="email" className={inp} value={form.email||''} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></div>
            <div><label className={lbl}>LinkedIn URL</label><input type="url" className={inp} value={form.linkedin||''} onChange={e=>setForm(p=>({...p,linkedin:e.target.value}))}/></div>
            <div><label className={lbl}>Display Order</label><input type="number" className={inp} value={form.order??0} onChange={e=>setForm(p=>({...p,order:+e.target.value}))}/></div>
            <div className="flex items-center gap-2 mt-5">
              <input type="checkbox" id="tpub" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <label htmlFor="tpub" className="text-sm text-gray-600 cursor-pointer">Published (visible on website)</label>
            </div>
            <div className="col-span-2"><label className={lbl}>Short Bio</label><textarea rows={3} className={`${inp} resize-none`} value={form.bio||''} onChange={e=>setForm(p=>({...p,bio:e.target.value}))}/></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {items.map(m => (
            <div key={m._id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
              {m.avatar
                ? <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0"/>
                : <div className="w-10 h-10 rounded-xl bg-royal-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{m.initials || m.name?.[0]}</div>
              }
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{m.name}</span>
                  {!m.isPublished && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}
                </div>
                <div className="text-xs text-gray-400">{m.role}{m.area ? ` · ${m.area}` : ''}</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => openEdit(m)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={() => setDeleting(m._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No team members yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   CORE VALUES  (P.U.R.P.O.S.E.)
══════════════════════════════════════════════════════════════════════════════ */
const ValuesSection = () => {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/about/admin/values')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const blank = { letter:'', label:'', description:'', isPublished:true, order:0 };
  const openEdit = (item) => { setEditing(item._id || 'new'); setForm(item._id ? {...item} : blank); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/about/admin/values', form);
      else await api.put(`/about/admin/values/${editing}`, form);
      setEditing(null); fetch();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/about/admin/values/${id}`); setDeleting(null); fetch(); };

  return (
    <>
      {deleting && <DeleteModal onConfirm={() => del(deleting)} onCancel={() => setDeleting(null)} />}
      <div className="flex justify-end mb-4">
        <button onClick={() => openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Value</button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">{editing === 'new' ? 'New Value' : 'Edit Value'}</h4>
            <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Letter (P/U/R/P/O/S/E)</label>
              <input className={inp} maxLength={1} placeholder="e.g. P" value={form.letter||''} onChange={e=>setForm(p=>({...p,letter:e.target.value.toUpperCase()}))}/>
            </div>
            <div>
              <label className={lbl}>Display Order</label>
              <input type="number" className={inp} value={form.order??0} onChange={e=>setForm(p=>({...p,order:+e.target.value}))}/>
            </div>
            <div className="col-span-2">
              <label className={lbl}>Label / Name *</label>
              <input className={inp} placeholder="e.g. Precision & Rigor" value={form.label||''} onChange={e=>setForm(p=>({...p,label:e.target.value}))}/>
            </div>
            <div className="col-span-2">
              <label className={lbl}>Description *</label>
              <textarea rows={3} className={`${inp} resize-none`} value={form.description||''} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <span className="text-sm text-gray-600">Published</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {items.map(v => (
            <div key={v._id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className="w-9 h-9 bg-royal-800 rounded-xl flex items-center justify-center text-gold-400 font-display font-bold text-base flex-shrink-0">{v.letter}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{v.label}</span>
                  {!v.isPublished && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}
                </div>
                <div className="text-xs text-gray-400 line-clamp-1">{v.description}</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => openEdit(v)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={() => setDeleting(v._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No values yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   STRATEGIC OBJECTIVES
══════════════════════════════════════════════════════════════════════════════ */
const ObjectivesSection = () => {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/about/admin/objectives')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const ICONS = ['brain','graduation','book','globe','flask','lightbulb','handshake','target','users'];
  const blank = { title:'', description:'', icon:'target', isPublished:true, order:0 };
  const openEdit = (item) => { setEditing(item._id || 'new'); setForm(item._id ? {...item} : blank); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/about/admin/objectives', form);
      else await api.put(`/about/admin/objectives/${editing}`, form);
      setEditing(null); fetch();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/about/admin/objectives/${id}`); setDeleting(null); fetch(); };

  return (
    <>
      {deleting && <DeleteModal onConfirm={() => del(deleting)} onCancel={() => setDeleting(null)} />}
      <div className="flex justify-end mb-4">
        <button onClick={() => openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Objective</button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">{editing === 'new' ? 'New Objective' : 'Edit Objective'}</h4>
            <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className={lbl}>Title *</label><input className={inp} value={form.title||''} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
            <div>
              <label className={lbl}>Icon</label>
              <select className={inp} value={form.icon||'target'} onChange={e=>setForm(p=>({...p,icon:e.target.value}))}>
                {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Order</label><input type="number" className={inp} value={form.order??0} onChange={e=>setForm(p=>({...p,order:+e.target.value}))}/></div>
            <div className="col-span-2"><label className={lbl}>Description *</label><textarea rows={3} className={`${inp} resize-none`} value={form.description||''} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <span className="text-sm text-gray-600">Published</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {items.map(o => (
            <div key={o._id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{o.title}</span>
                  {!o.isPublished && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}
                </div>
                <div className="text-xs text-gray-400 line-clamp-1">{o.description}</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => openEdit(o)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={() => setDeleting(o._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No objectives yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   DEPARTMENTS
══════════════════════════════════════════════════════════════════════════════ */
const DepartmentsSection = () => {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [newItem,  setNewItem]  = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/about/admin/departments')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const ICONS   = ['flask','graduation','lightbulb','handshake','brain','book','globe','users','target'];
  const COLORS  = ['blue','emerald','violet','amber','rose','teal','royal'];
  const blank   = { title:'', icon:'flask', color:'blue', items:[], isPublished:true, order:0 };
  const openEdit = (item) => { setEditing(item._id || 'new'); setForm(item._id ? {...item, items:[...(item.items||[])]} : blank); setNewItem(''); };
  const addItem  = () => { if (!newItem.trim()) return; setForm(p => ({...p, items:[...p.items, newItem.trim()]})); setNewItem(''); };
  const removeItem = (i) => setForm(p => ({...p, items: p.items.filter((_,idx) => idx !== i)}));

  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/about/admin/departments', form);
      else await api.put(`/about/admin/departments/${editing}`, form);
      setEditing(null); fetch();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/about/admin/departments/${id}`); setDeleting(null); fetch(); };

  return (
    <>
      {deleting && <DeleteModal onConfirm={() => del(deleting)} onCancel={() => setDeleting(null)} />}
      <div className="flex justify-end mb-4">
        <button onClick={() => openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Department</button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">{editing === 'new' ? 'New Department' : 'Edit Department'}</h4>
            <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="col-span-2"><label className={lbl}>Department Title *</label><input className={inp} value={form.title||''} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
            <div>
              <label className={lbl}>Icon</label>
              <select className={inp} value={form.icon||'flask'} onChange={e=>setForm(p=>({...p,icon:e.target.value}))}>
                {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Color Theme</label>
              <select className={inp} value={form.color||'blue'} onChange={e=>setForm(p=>({...p,color:e.target.value}))}>
                {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Order</label><input type="number" className={inp} value={form.order??0} onChange={e=>setForm(p=>({...p,order:+e.target.value}))}/></div>
            <div className="flex items-center gap-2 mt-5">
              <input type="checkbox" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <span className="text-sm text-gray-600">Published</span>
            </div>
          </div>

          {/* Sub-items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className={lbl}>Sub-Programs / Focus Areas</label>
            </div>
            <div className="space-y-2 mb-2">
              {(form.items||[]).map((item, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm">{item}</div>
                  <button onClick={() => removeItem(i)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg"><X size={14}/></button>
                </div>
              ))}
              {(form.items||[]).length === 0 && <p className="text-xs text-gray-400">No items yet. Add below.</p>}
            </div>
            <div className="flex gap-2">
              <input className={`${inp} flex-1`} placeholder="e.g. Explainable & Educational AI (XAI-E)"
                value={newItem} onChange={e=>setNewItem(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}/>
              <button onClick={addItem} className="px-4 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold hover:bg-royal-900 transition-colors flex-shrink-0">
                <Plus size={15}/>
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Press Enter or click + to add each item</p>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {items.map(d => (
            <div key={d._id} className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm text-gray-900">{d.title}</span>
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-[10px] font-semibold capitalize">{d.color} · {d.icon}</span>
                  {!d.isPublished && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}
                </div>
                <div className="text-xs text-gray-400">{(d.items||[]).slice(0,3).join(' · ')}{(d.items||[]).length > 3 ? ` + ${d.items.length-3} more` : ''}</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => openEdit(d)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={() => setDeleting(d._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No departments yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function AdminAbout() {
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const seed = async () => {
    setSeeding(true); setSeedMsg('');
    try {
      await api.get('/about/seed');
      setSeedMsg('success');
      setTimeout(() => setSeedMsg(''), 3000);
      window.location.reload();
    } catch (e) { setSeedMsg('error:' + e.message); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Info size={22} className="text-royal-800" /> About Page Content
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage all content shown on the public About Us page — team, values, objectives, and departments.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {seedMsg === 'success' && <span className="text-emerald-600 text-sm flex items-center gap-1"><CheckCircle size={14}/>Seeded!</span>}
          {seedMsg.startsWith('error:') && <span className="text-red-600 text-sm">{seedMsg.slice(6)}</span>}
          <button onClick={seed} disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-60">
            {seeding ? <Loader2 size={15} className="animate-spin"/> : <RefreshCw size={15}/>}
            Seed Default Data
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
        <div>
          <strong>First time?</strong> Click "Seed Default Data" to populate all sections with SAAI's default content.
          The seed will only add missing records — it won't overwrite anything you've already edited.
          Mission, Vision, and the page tagline are edited in <strong>Admin → Site Settings</strong>.
        </div>
      </div>

      <Section title="Team Members" subtitle="Leadership and staff shown on the About page" icon={Users} defaultOpen>
        <TeamSection />
      </Section>

      <Section title="Core Values (P.U.R.P.O.S.E.)" subtitle="The 7 principle values displayed on the About page" icon={Star}>
        <ValuesSection />
      </Section>

      <Section title="Strategic Objectives" subtitle="The 4 strategic mandate cards on the About page" icon={Target}>
        <ObjectivesSection />
      </Section>

      <Section title="Departments" subtitle="The 4 department cards with their sub-program lists" icon={Building2}>
        <DepartmentsSection />
      </Section>
    </div>
  );
}